import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// import { Textarea } from "@/components/ui/textarea";
import { Editor } from "@tinymce/tinymce-react";
import { callUpdatePost } from "@/services/ophim69Service";
import { showToast } from "@/lib/toast";

export interface IPostForm {
  name: string;
  description: string;
}

interface ModalUpdatePostProps {
  isOpen: boolean;
  onClose: () => void;
  post?: any | null;
  onCompleted?: () => void;
}

export default function ModalUpdatePost({
  isOpen,
  onClose,
  post,
  onCompleted,
}: ModalUpdatePostProps) {
  const [form, setForm] = useState<IPostForm>({ name: "", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: post?.name || "",
        description: post?.description || "",
      });
      setThumbnailFile(null);
      setThumbnailPreview("");
    }
  }, [isOpen, post]);

  const currentThumb = useMemo(() => {
    const t = post?.thumbnail as string | undefined;
    if (!t) return "";
    if (t.startsWith("http")) return t;
    return `https://api.ophim69.com/api/v1/${t}`;
  }, [post]);

  const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return; // 5MB
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  // Drag & drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dropRef.current?.classList.add("border-blue-400");
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dropRef.current?.classList.remove("border-blue-400");
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
    dropRef.current?.classList.add("border-blue-400");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dropRef.current?.classList.remove("border-blue-400");
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024)
        return;
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async () => {
    if (!post?._id) return;
    if (!form.name.trim()) return;

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      if (form.description?.trim()) fd.append("description", form.description);
      if (thumbnailFile) fd.append("thumbnail", thumbnailFile);

      const res: any = await callUpdatePost(post._id, fd);
      // apiOphim69Client returns response body directly; consider success when truthy
      if (res) {
        onCompleted?.();
        onClose();
        showToast.success("Cập nhật post thành công");
      } else {
        showToast.error("Cập nhật post thất bại");
      }
    } catch (_e) {
      showToast.error("Có lỗi xảy ra khi cập nhật post");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className=" p-0 !max-w-[90vw]">
        <div className="max-h-[85vh] overflow-y-auto p-6 scrollbar-gentle">
          <DialogHeader>
            <DialogTitle>Cập nhật post</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            <div>
              <label className="block text-sm font-medium mb-2">Tên post</label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Nhập tên post..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Mô tả</label>
              <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                licenseKey="gpl"
                value={form.description}
                onEditorChange={(content) =>
                  setForm((p) => ({ ...p, description: content }))
                }
                init={{
                  height: 380,
                  menubar: false,
                  branding: false,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "preview",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | bold italic underline forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | link image media | code fullscreen",
                  content_style:
                    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                  placeholder: "Nhập mô tả chi tiết cho post...",
                }}
              />
            </div>

            {currentThumb && !thumbnailPreview && (
              <div>
                <label className="block text-sm font-medium mb-3">
                  Thumbnail hiện tại
                </label>
                <div className="flex justify-center">
                  <div className="relative w-48 h-64 border rounded-lg overflow-hidden shadow-sm">
                    <img
                      src={currentThumb}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                      Thumbnail hiện tại
                    </div>
                  </div>
                </div>
              </div>
            )}

            {thumbnailPreview && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Thumbnail mới
                </label>
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail mới"
                  className="w-32 h-44 object-cover rounded border"
                />
              </div>
            )}

            <div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={fileInputRef}
                onChange={onChangeFile}
              />
              <div
                ref={dropRef}
                className={`w-full border-2 border-dashed rounded-lg flex flex-col items-center justify-center p-6 transition-colors ${
                  isDragging
                    ? "border-blue-400 bg-blue-50/40"
                    : "border-gray-300"
                }`}
                style={{ minHeight: 140 }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className="text-gray-400"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M12 16V8m0 0l-3 3m3-3 3 3M20.25 16.5V19A2.25 2.25 0 0 1 18 21.25H6A2.25 2.25 0 0 1 3.75 19v-2.5m16.5 0V8.25A2.25 2.25 0 0 0 18 6H6A2.25 2.25 0 0 0 3.75 8.25v8.25m16.5 0-4.72-4.72a2.25 2.25 0 0 0-3.18 0l-1.53 1.53a2.25 2.25 0 0 1-3.18 0L3.75 16.5"
                  />
                </svg>
                <div className="mt-2 text-sm text-gray-600">
                  Kéo & thả hoặc bấm để thêm thumbnail
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  (Chỉ nhận file ảnh, tối đa 5MB)
                </div>
                {thumbnailPreview && (
                  <div className="mt-4 flex items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview("");
                      }}
                      className="cursor-pointer"
                    >
                      Xoá ảnh đã chọn
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Huỷ
              </Button>
              <Button onClick={onSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Đang lưu..." : "Lưu"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
